import { useCallback } from "react";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import { Paginated, PaginationQuery, UpdateOrderRequestBody, UpdateVisibilityRequestBody } from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import { HrailTrolley, InsertHrailTrolleyBody, UpdateHrailTrolleyBody } from "./types/trolleys";

export const useHrailTrolleysApi = () => {
  const api = useApi();

  const getAdminHrailTrolley = useCallback(
    (trolleyId: string, signal: AbortSignal) => {
      const url = `/admin/settings/hrail/trolleys/${trolleyId}`;
      return useSWRMutation<HrailTrolley, CustomError, string>(url, (url) =>
        api.get<HrailTrolley>({ url, signal }),
      );
    },
    [api],
  );

  const useAdminHrailTrolleys = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/hrail/trolleys`, query);
      return useSWRImmutable<Paginated<HrailTrolley>, CustomError, string>(url, (url) =>
        api.get<Paginated<HrailTrolley>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminHrailTrolleys = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/hrail/trolleys`, query);
      return api.get<Paginated<HrailTrolley>>({ url, signal }).then((x) => x.items);
    },
    [api],
  );

  const insertAdminHrailTrolley = useCallback(
    async (body: InsertHrailTrolleyBody): Promise<{ id: string }> => {
      const id = await api.post<{ id: string }, InsertHrailTrolleyBody>(`/admin/settings/hrail/trolleys`, body);
      mutateCache([`/admin/settings/hrail/trolleys`, `/settings/hrail`]);
      return id;
    },
    [api],
  );

  const updateAdminHrailTrolley = useCallback(
    async (trolleyId: string, body: UpdateHrailTrolleyBody): Promise<void> => {
      const url = `/admin/settings/hrail/trolleys/${trolleyId}`;
      await api.put<UpdateHrailTrolleyBody>(url, body);
      mutateCache([`/admin/settings/hrail/trolleys`, `/settings/hrail`], url);
    },
    [api],
  );

  const updateAdminHrailTrolleyVisibility = useCallback(
    async (trolleyId: string, visibility: UpdateVisibilityRequestBody): Promise<void> => {
      const url = `/admin/settings/hrail/trolleys/${trolleyId}/visibility`;
      await api.put<UpdateVisibilityRequestBody>(url, visibility);
      mutateCache([`/admin/settings/hrail/trolleys`, `/settings/hrail`]);
    },
    [api],
  );

  const updateAdminHrailTrolleyOrder = useCallback(
    async (trolleyId: string, order: UpdateOrderRequestBody): Promise<void> => {
      const url = `/admin/settings/hrail/trolleys/${trolleyId}/order`;
      await api.put<UpdateOrderRequestBody>(url, order);
      mutateCache([`/admin/settings/hrail/trolleys`, `/settings/hrail`]);
    },
    [api],
  );

  const deleteAdminHrailTrolley = useCallback(
    async (trolleyId: string): Promise<void> => {
      await api.del(`/admin/settings/hrail/trolleys/${trolleyId}`);
      mutateCache([`/admin/settings/hrail/trolleys`, `/settings/hrail`]);
    },
    [api],
  );

  return {
    getAdminHrailTrolley,
    useAdminHrailTrolleys,
    getAdminHrailTrolleys,
    insertAdminHrailTrolley,
    updateAdminHrailTrolley,
    updateAdminHrailTrolleyVisibility,
    updateAdminHrailTrolleyOrder,
    deleteAdminHrailTrolley,
  };
};
